import { styled, Theme } from '@mui/material/styles';
import { NaosButton } from '@dtsl/react';
import EndCallButton from '../Buttons/EndCallButton/EndCallButton';
import FlipCameraButton from './FlipCameraButton/FlipCameraButton';
import Menu from './Menu/Menu';
import useRoomState from '../../hooks/useRoomState/useRoomState';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { Typography, Grid, Box, Tooltip, Button as MuiButton, ButtonProps } from '@mui/material';
import ToggleAudioButton from '../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../Buttons/ToggleVideoButton/ToggleVideoButton';
import ToggleChatButton from '../Buttons/ToggleChatButton/ToggleChatButton';
import ToggleScreenShareButton from '../Buttons/ToogleScreenShareButton/ToggleScreenShareButton';
import { useTranslation } from 'react-i18next';

const Container = styled('footer')<{ theme?: Theme }>(({ theme }) => ({
  backgroundColor: 'var(--sib-color_primary-default)',
  bottom: 0,
  left: 0,
  right: 0,
  height: `${theme.footerHeight}px`,
  position: 'relative',
  display: 'flex',
  padding: '0 1.43em',
  zIndex: 10,
  [theme.breakpoints.down('sm')]: {
    height: `${theme.mobileFooterHeight}px`,
    padding: 0,
  },
}));

const ScreenShareBanner = styled(Grid)<{ theme?: Theme }>(({ theme }) => ({
  position: 'absolute',
  zIndex: 10,
  bottom: `${theme.footerHeight}px`,
  left: 0,
  right: 0,
  height: '104px',
  background: 'rgba(0, 0, 0, 0.5)',
  '& h6': {
    color: 'white',
  },
  '& button': {
    background: 'white',
    color: '#eb4d5c',
    border: `2px solid #eb4d5c`,
    margin: '0 2em',
    '&:hover': {
      color: '#d34552',
      border: `2px solid #d34552`,
      background: '#FFE9E7',
    },
  },
}));

export const StyledControlButton = styled(MuiButton)<ButtonProps>(() => ({
  height: '100%',
  flexDirection: 'column',
  color: '#fff',
  margin: 0,
  '& span': {
    margin: 0,
  },
}));

export const StyledScreenShareButton = styled(StyledControlButton)<ButtonProps>(() => ({
  '&[disabled]': {
    color: '#bbb',
    '& svg *': {
      fill: '#bbb',
    },
  },
}));

export default function MenuBar() {
  const { isSharingScreen, toggleScreenShare } = useVideoContext();
  const roomState = useRoomState();
  const { t } = useTranslation();
  const isReconnecting = roomState === 'reconnecting';

  return (
    <Container>
      {isSharingScreen && (
        <ScreenShareBanner container justifyContent="center" alignItems="center">
          <Typography variant="subtitle1">{t('ROOM.SCREEN_SHARE_INFO')}</Typography>
          <Tooltip
            title={t('ROOM.STOP_SCREEN_SHARE_TEXT_TOOLTIP')}
            placement="top"
            PopperProps={{ disablePortal: true }}
          >
            <NaosButton
              id="video-calls-stop-screen-share"
              variant="secondary"
              color="destructive"
              label={t('ROOM.STOP_SCREEN_SHARE_TEXT')}
              onClick={() => toggleScreenShare()}
            ></NaosButton>
          </Tooltip>
        </ScreenShareBanner>
      )}
      <Grid container justifyContent="space-around" alignItems="center">
        <Box sx={{ display: { xs: 'none', md: 'block' }, flex: 1 }}>
          <Grid style={{ flex: 1 }} container alignItems="center">
            <Menu CustomToggleButton={StyledControlButton} />
          </Grid>
        </Box>
        <Grid item>
          <Grid container justifyContent="center">
            <ToggleAudioButton
              iconFill="#fff"
              controls
              disabled={isReconnecting}
              CustomToggleButton={StyledControlButton}
            />
            <ToggleVideoButton
              iconFill="#fff"
              controls
              disabled={isReconnecting}
              CustomToggleButton={StyledControlButton}
            />
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              {!isSharingScreen && (
                <ToggleScreenShareButton
                  iconFill="#fff"
                  disabled={isReconnecting}
                  CustomToggleButton={StyledScreenShareButton}
                />
              )}
            </Box>
            <FlipCameraButton iconFill="#fff" CustomToggleButton={StyledControlButton} />
            <ToggleChatButton iconFill="#fff" CustomToggleButton={StyledControlButton} />
          </Grid>
        </Grid>
        <Box sx={{ display: { xs: 'none', md: 'block' }, flex: 1 }}>
          <Grid style={{ flex: 1 }}>
            <Grid container justifyContent="flex-end">
              <EndCallButton />
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Container>
  );
}
